import Link from 'next/link'
import React from 'react'

interface PaginationItemProps {
  page: number | string
  selected: boolean
  type: 'page' | 'next' | 'previous' | 'ellipsis'
  href?: string
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  disabled?: boolean
}

interface PaginationProps {
  count: number
  page: number
  onChange?: (event: React.MouseEvent<HTMLButtonElement>, page: number) => void
  createHref?: (page: number) => string
  className?: string
  renderItem?: (props: PaginationItemProps) => React.ReactNode
}

const DefaultPaginationItem = ({
  page,
  selected,
  type,
  href,
  onClick,
  disabled
}: PaginationItemProps) => {
  const baseButtonStyles = `
    h-8 w-8 flex items-center justify-center rounded-full
    transition-colors duration-200 ease-in-out font-sans
    focus:outline-none focus:ring-2 focus:ring-blue-500
  `

  const activeButtonStyles = `
    bg-blue-500 text-white-500 hover:bg-blue-600
  `

  const inactiveButtonStyles = `
    text-black-500 hover:bg-grey-300
  `

  const disabledButtonStyles = `
    text-ink-200 cursor-not-allowed
  `

  if (type === 'ellipsis') {
    return <span className="px-2 text-black-500">...</span>
  }

  const content = type === 'next' ? '›' : type === 'previous' ? '‹' : page

  // If we have an onClick handler, render a button
  if (onClick) {
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={`${baseButtonStyles} ${
          selected
            ? activeButtonStyles
            : disabled
              ? disabledButtonStyles
              : inactiveButtonStyles
        }`}
        aria-current={selected ? 'page' : undefined}
        aria-label={`${type === 'page' ? 'Page ' : ''}${page}`}
      >
        {content}
      </button>
    )
  }

  // Otherwise, render a Link or just the content if disabled/no href
  const buttonContent = (
    <div
      className={`${baseButtonStyles} ${
        selected
          ? activeButtonStyles
          : disabled
            ? disabledButtonStyles
            : inactiveButtonStyles
      }`}
      aria-current={selected ? 'page' : undefined}
      aria-label={`${type === 'page' ? 'Page ' : ''}${page}`}
    >
      {content}
    </div>
  )

  if (disabled || !href) {
    return buttonContent
  }

  return (
    <Link href={href} className="cursor-pointer">
      {buttonContent}
    </Link>
  )
}

const Pagination: React.FC<PaginationProps> = ({
  count,
  page,
  onChange,
  createHref,
  className = '',
  renderItem = DefaultPaginationItem
}) => {
  const getPageNumbers = (): (number | string)[] => {
    if (count <= 1) return []

    if (count <= 7) {
      return Array.from({ length: count }, (_, i) => i + 1)
    }

    const pages = new Set<number>()
    pages.add(1)
    pages.add(count)
    pages.add(page)
    if (page > 1) pages.add(page - 1)
    if (page < count) pages.add(page + 1)

    if (page <= 3) {
      for (let i = 1; i <= Math.min(4, count); i++) pages.add(i)
    }

    if (page >= count - 2) {
      for (let i = count; i >= Math.max(count - 3, 1); i--) pages.add(i)
    }

    const sorted = Array.from(pages).sort((a, b) => a - b)
    const result: (number | string)[] = []

    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
        result.push('...')
      }
      result.push(sorted[i])
    }

    return result
  }

  const handlePageClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    pageNumber: number
  ) => {
    onChange?.(event, pageNumber)
  }

  if (count <= 1) return null

  return (
    <nav
      className={`flex items-center justify-center space-x-1 ${className}`}
      aria-label="Pagination"
    >
      {renderItem({
        page: page - 1,
        type: 'previous',
        selected: false,
        href: !onChange && createHref ? createHref(page - 1) : undefined,
        onClick: onChange ? (e) => handlePageClick(e, page - 1) : undefined,
        disabled: page === 1
      })}

      {getPageNumbers().map((pageNumber, index) => (
        <React.Fragment key={`page-${index}`}>
          {pageNumber === '...'
            ? renderItem({
                page: pageNumber,
                type: 'ellipsis',
                selected: false
              })
            : renderItem({
                page: pageNumber as number,
                type: 'page',
                selected: page === pageNumber,
                href:
                  !onChange && createHref
                    ? createHref(pageNumber as number)
                    : undefined,
                onClick: onChange
                  ? (e) => handlePageClick(e, pageNumber as number)
                  : undefined
              })}
        </React.Fragment>
      ))}

      {renderItem({
        page: page + 1,
        type: 'next',
        selected: false,
        href:
          !onChange && createHref && page < count
            ? createHref(page + 1)
            : undefined,
        onClick:
          onChange && page < count
            ? (e) => handlePageClick(e, page + 1)
            : undefined,
        disabled: page >= count
      })}
    </nav>
  )
}

export default Pagination
